import { isEmpty, isNotEmpty } from '@newfront-insurance/core';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useCallback, useEffect, useMemo } from 'react';

import { useAttachments } from './attachments-provider';
import { useChatInputContext, useDragHandlersRef } from './chat-input-context';
import { PromptEditor } from './prompt-editor';
import { SkillMenu, useSkillMenu } from './skill-menu';
import { useSkillCommands } from '../../../../hooks/use-skill-commands';
import { hasUnsyncedAttachments } from '../../../../lib/chat-workspace';
import {
  findActiveSkillQuery,
  findFirstMatchingSkillCommand,
  findMatchingSkillCommand,
  findNextSelectableSkillCommandIndex,
  isSkillCommandDisabledByAccount,
  replaceActiveSkillQuery,
} from '../../../../lib/skill-command-utils';
import type { SkillCommand } from '../../../../lib/skill-constants';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import {
  getCountBucket,
  getLengthBucket,
  getSkillCommandAnalyticsProperties,
  NOVA_ANALYTICS_EVENTS,
  trackNovaEvent,
} from '../../lib/analytics';
import { filterSkillCommands } from '../skill/use-skill-list-state';

interface ChatInputTextareaProps {
  placeholder?: string;
  className?: string;
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
  skillsAccountUuid?: string;
  preferSkillsContext?: boolean;
  submitOnEnter?: boolean;
  onPasteFiles?: (event: React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>) => void;
}

const EMPTY_SKILL_COMMANDS: SkillCommand[] = [];

/**
 * Chat prompt editor wrapper.
 * Handles text input, auto-height adjustment, skill command autocomplete,
 * paste analytics, and submit shortcuts around the Lexical editor primitive.
 */
export function ChatInputTextarea({
  placeholder = 'Ask a question...',
  className,
  id,
  ariaLabel = placeholder,
  disabled = false,
  skillsAccountUuid,
  preferSkillsContext = true,
  submitOnEnter = true,
  onPasteFiles,
}: ChatInputTextareaProps) {
  const { input, setInput, editorRef, promptEditorRef, handleSubmit, status, maxLength } = useChatInputContext();
  const { accountUuid, selectedAccount, chatUuid, userUuid, additionalContext } = useChatOverlayContext();
  const analytics = useAnalytics();
  const { attachments } = useAttachments();
  const handlersRef = useDragHandlersRef();
  const handlePaste = onPasteFiles ?? handlersRef.current.handlePaste;
  const hasUploadingFiles = attachments.some((attachment) => attachment.isUploading);
  const hasUnsyncedAttachmentContext = hasUnsyncedAttachments(attachments, additionalContext);

  // Skill commands need a signed-in user; hosts without one (Benji) keep a plain input
  const skillsEnabled = !isEmpty(userUuid);

  // Skill menu state
  const skillMenu = useSkillMenu();
  const { commands } = useSkillCommands({
    accountUuid: preferSkillsContext ? accountUuid : skillsAccountUuid,
    userUuid,
    preferContext: preferSkillsContext,
  });
  const activeCommands = skillsEnabled ? commands : EMPTY_SKILL_COMMANDS;
  const filteredSkillCommands = useMemo(
    () => filterSkillCommands(activeCommands, skillMenu.searchQuery),
    [activeCommands, skillMenu.searchQuery],
  );
  const hasAccountInContext = preferSkillsContext
    ? Boolean(accountUuid ?? selectedAccount?.uuid)
    : Boolean(skillsAccountUuid);
  const isCommandDisabled = useCallback(
    (command: SkillCommand) => isSkillCommandDisabledByAccount(command, hasAccountInContext),
    [hasAccountInContext],
  );
  const adjustHeight = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = `${editorRef.current.scrollHeight + 2}px`;
    }
  }, [editorRef]);

  const resetHeight = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = '94px';
    }
  }, [editorRef]);

  const handlePromptEditorChange = useCallback(
    (newValue: string, cursorPosition: number) => {
      setInput(newValue);
      adjustHeight();

      if (!skillsEnabled) {
        return;
      }

      const activeQuery = findActiveSkillQuery(newValue, cursorPosition);
      const matchedCommand = findMatchingSkillCommand(newValue, activeCommands, cursorPosition);

      if (activeQuery && !matchedCommand) {
        const nextFilteredCommands = filterSkillCommands(activeCommands, activeQuery.query);
        const nextSelectedIndex = findNextSelectableSkillCommandIndex(nextFilteredCommands, -1, 1, isCommandDisabled);
        if (!skillMenu.isOpen) {
          trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_MENU_OPENED, {
            chatUuid,
            queryLengthBucket: getLengthBucket(activeQuery.query.length),
            resultCountBucket: getCountBucket(activeCommands.length),
            skillResultCountBucket: getCountBucket(activeCommands.length),
          });
          skillMenu.open(activeQuery.query, nextSelectedIndex);
        } else {
          skillMenu.updateQuery(activeQuery.query, nextSelectedIndex);
        }
      } else if (skillMenu.isOpen) {
        skillMenu.close();
      }
    },
    [activeCommands, adjustHeight, analytics, chatUuid, isCommandDisabled, setInput, skillMenu, skillsEnabled],
  );

  // Handle command selection from menu
  const handleSkillSelect = useCallback(
    (command: SkillCommand) => {
      const cursorPosition = promptEditorRef.current?.getCursorPosition() ?? input.length;
      const { nextValue, nextCursorPosition, commandStartIndex, commandEndIndex } = replaceActiveSkillQuery(
        input,
        command.handle,
        cursorPosition,
      );

      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_COMMAND_SELECTED, {
        ...getSkillCommandAnalyticsProperties(command),
        chatUuid,
        queryLengthBucket: getLengthBucket(skillMenu.searchQuery.length),
      });

      skillMenu.close();
      if (command.skill) {
        promptEditorRef.current?.setSkillCommand(
          nextValue,
          {
            skillId: command.skill.id,
            startIndex: commandStartIndex,
            endIndex: commandEndIndex,
          },
          nextCursorPosition,
        );
      } else {
        promptEditorRef.current?.setPlainText(nextValue, nextCursorPosition);
      }
      promptEditorRef.current?.focus();
    },
    [analytics, chatUuid, input, promptEditorRef, skillMenu],
  );

  const handleCommandMenuNavigate = useCallback(
    (delta: number) => {
      if (filteredSkillCommands.length === 0) {
        skillMenu.setSelectedIndex(-1);
        return;
      }

      skillMenu.setSelectedIndex((currentIndex) =>
        findNextSelectableSkillCommandIndex(filteredSkillCommands, currentIndex, delta, isCommandDisabled),
      );
    },
    [filteredSkillCommands, isCommandDisabled, skillMenu],
  );

  const handleCommandMenuSelect = useCallback(() => {
    const selectedCommand = filteredSkillCommands[skillMenu.selectedIndex] ?? filteredSkillCommands[0];
    if (selectedCommand && !isCommandDisabled(selectedCommand)) {
      handleSkillSelect(selectedCommand);
    }
  }, [filteredSkillCommands, handleSkillSelect, isCommandDisabled, skillMenu.selectedIndex]);

  const submitForm = useCallback(() => {
    if (hasUploadingFiles || hasUnsyncedAttachmentContext) {
      return;
    }

    // handleSubmit enforces the limit too, but bail here first so we don't run the
    // height reset / contenteditable clear on a submission that will be rejected.
    if (maxLength !== undefined && input.trim().length > maxLength) {
      return;
    }

    handleSubmit(undefined);
    resetHeight();
    promptEditorRef.current?.setPlainText('', 0);
  }, [hasUploadingFiles, hasUnsyncedAttachmentContext, maxLength, input, handleSubmit, promptEditorRef, resetHeight]);

  // Initial height adjustment
  useEffect(() => {
    if (editorRef.current) {
      adjustHeight();
    }
  }, [adjustHeight, editorRef]);

  // While the skill menu is open, intercept Esc at the document capture phase so
  // we close the menu before Radix's DismissableLayer or Nova's keydown handler
  // see the event. Without this, the contenteditable can blur (default Esc
  // behavior) and Nova's document-level handler can close the entire chat.
  const { isOpen: isSkillMenuOpen, close: closeSkillMenu } = skillMenu;
  useEffect(() => {
    if (!isSkillMenuOpen) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      closeSkillMenu();
    };

    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [isSkillMenuOpen, closeSkillMenu]);

  const handlePromptEditorPaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      const text = event.clipboardData.getData('text/plain');
      const html = event.clipboardData.getData('text/html');
      const fileCount = event.clipboardData.files?.length ?? 0;
      const contentType = fileCount > 0 && text ? 'mixed' : fileCount > 0 ? 'files' : html ? 'html' : 'text';
      const selectedCommand = findFirstMatchingSkillCommand(input, activeCommands);
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_INPUT_PASTED, {
        contentType,
        fileCountBucket: getCountBucket(fileCount),
        hasFiles: fileCount > 0,
        hasHtml: Boolean(html),
        inputLengthBeforeBucket: getLengthBucket(input.length),
        lineCountBucket: getCountBucket(text ? text.split(/\r\n|\r|\n/).length : 0),
        plainTextLengthBucket: getLengthBucket(text.length),
        selectedCommandHandle: selectedCommand?.command.handle,
      });

      // Also call the external paste handler for file handling
      if (handlePaste) {
        handlePaste(event as unknown as React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>);
      }
    },
    [activeCommands, analytics, handlePaste, input],
  );

  const editorClassName = cn(className, {
    'rounded-b-[0px] border-b-0': isNotEmpty(attachments),
  });

  return (
    <div className="relative flex w-full flex-col gap-4">
      {skillsEnabled ? (
        <SkillMenu
          isOpen={skillMenu.isOpen}
          onClose={skillMenu.close}
          onSelect={handleSkillSelect}
          commands={activeCommands}
          searchQuery={skillMenu.searchQuery}
          selectedIndex={skillMenu.selectedIndex}
          onSelectedIndexChange={skillMenu.setSelectedIndex}
        />
      ) : null}
      <PromptEditor
        ref={promptEditorRef}
        id={id}
        ariaLabel={ariaLabel}
        value={input}
        commands={activeCommands}
        placeholder={placeholder}
        className={editorClassName}
        editorRef={editorRef}
        disabled={disabled}
        submitOnEnter={submitOnEnter}
        isCommandMenuOpen={skillsEnabled && skillMenu.isOpen}
        canSubmit={!disabled && status === 'ready' && !hasUploadingFiles && !hasUnsyncedAttachmentContext}
        onChange={handlePromptEditorChange}
        onCommandMenuClose={skillMenu.close}
        onCommandMenuNavigate={handleCommandMenuNavigate}
        onCommandMenuSelect={handleCommandMenuSelect}
        onPaste={handlePromptEditorPaste}
        onSubmit={submitForm}
      />
    </div>
  );
}
